/**
 * Shared column builders for University tables
 * Similar to billing/_utils.tsx but specialized for university student and internship tables
 */

import {
  Row,
  buildBulkSelectionColumn,
  buildAvatarDetailsColumn,
  buildTwoLineColumn,
  buildActionColumn
} from "@/components/Table";
import Icon from "@/components/Icon";
import Tooltip from "@/components/Tooltip";
import Tag from "@/components/Tag";
import type { TagType } from "@/components/Tag/Tag";
import { toFormat } from "@/lib/dateHelpers";
import { isCompactLayout } from "@/assets/theme/sizes";
import { StudentBasedRow, InternshipBasedRow } from "./types";
import { CRMNote } from "@/types";
import type { GetCohortEnrollmentNotesByUniversityQuery } from "../[universityId]/students/[studentId]/notes/queries";
import { ReactNode } from "react";

export { buildActionColumn };

// ==========================================
// EXPANSION STATE TYPES
// ==========================================

/**
 * Type for tracking which column is expanded in a row
 * null = row collapsed, "notes" = notes expanded, "programLevels" = program levels expanded
 */
export type ExpandedColumn = "notes" | "programLevels" | null;

/**
 * Map of row IDs to their expansion state
 * Example: { "row-1": "notes", "row-2": "programLevels" }
 */
export type ExpansionState = Record<string, ExpandedColumn>;

// ==========================================
// STUDENT-BASED COLUMN BUILDERS
// ==========================================

interface BuildStudentColumnsProps<T extends StudentBasedRow> {
  isCompactLayout: boolean;
  hasCheckbox?: boolean;
  additionalMobileFields?: (row: T) => ReactNode;
  /**
   * When true, the Name (avatar details) column becomes a sortable control
   * whose `id` matches the backend
   * `BackofficeEducationUniversityStudentsSortField.LAST_NAME` enum value
   * so it can drive server-side sorting through `orderBy`.
   */
  sortableName?: boolean;
}

/**
 * Builds standard columns for student-based tables
 * Similar to billing's buildColumns but for university students
 *
 */
export function buildStudentColumns<T extends StudentBasedRow>({
  isCompactLayout,
  hasCheckbox = false,
  additionalMobileFields,
  sortableName = false
}: BuildStudentColumnsProps<T>) {
  return [
    ...(hasCheckbox ? [buildBulkSelectionColumn<T>()] : []),
    ...buildAvatarDetailsColumn<T>({
      isCompactLayout,
      getTitle: (row) => row.name,
      getSubtitle: (row) => `PGA ID: ${row.pgaId}`,
      getPicture: (row) => row.profilePhoto,
      additionalMobileFields,
      size: 280,
      minSize: 220,
      ...(sortableName ? { id: "LAST_NAME", sortType: "text" as const, header: "Name" } : {})
    })
  ];
}

// ==========================================
// INTERNSHIP-BASED COLUMN BUILDERS
// ==========================================

interface BuildInternshipColumnsProps<T extends InternshipBasedRow> {
  isCompactLayout: boolean;
  additionalMobileFields?: (row: T) => ReactNode;
}

/**
 * Builds standard columns for internship-based tables
 * Internships display student information similar to student tables
 *
 */
export function buildInternshipColumns<T extends InternshipBasedRow>({
  isCompactLayout,
  additionalMobileFields
}: BuildInternshipColumnsProps<T>) {
  return buildAvatarDetailsColumn<T>({
    isCompactLayout,
    getTitle: (row) => `${row.student.firstName} ${row.student.lastName}`,
    getSubtitle: (row) => `PGA ID: ${row.student.pgaId}`,
    getPicture: (row) => row.student.profilePhoto,
    additionalMobileFields
  });
}

// ==========================================
// NOTES COLUMN BUILDER
// ==========================================

interface BuildNotesColumnProps<T> {
  expanded: ExpansionState;
  onExpand: (rowId: string, column: ExpandedColumn) => void;
  getStudentId: (row: T) => string;
  // Flexible: accept either a map or a function to get notes count
  studentNotesMap?: Map<string, number>;
  getNotesCount?: (row: T) => number;
}

/**
 * Builds a standardized Notes column for student tables
 * Shows note count with expand/collapse functionality
 *
 * Usage:
 * ```typescript
 * // Option 1: Using a pre-built map (more efficient for large datasets)
 * const columns = [
 *   ...buildStudentColumns({ ... }),
 *   buildNotesColumn({
 *     studentNotesMap,
 *     expanded,
 *     onExpand,
 *     getStudentId: (row) => row.studentId
 *   }),
 *   // other columns...
 * ];
 *
 * // Option 2: Using a function (simpler, no map needed)
 * const columns = [
 *   ...buildStudentColumns({ ... }),
 *   buildNotesColumn({
 *     getNotesCount: (row) => row.notes?.length || 0,
 *     expanded,
 *     onExpand,
 *     getStudentId: (row) => row.studentId
 *   }),
 *   // other columns...
 * ];
 * ```
 */
export function buildNotesColumn<T>({
  studentNotesMap,
  getNotesCount,
  expanded,
  onExpand,
  getStudentId
}: BuildNotesColumnProps<T>) {
  return {
    id: "notes",
    header: "Notes",
    enableSorting: false,
    cell: ({ row }: { row: Row<T> }) => {
      const notesCount = getNotesCount
        ? getNotesCount(row.original)
        : studentNotesMap?.get(getStudentId(row.original)) || 0;
      const rowExpansions = expanded[row.id];
      const isExpanded = rowExpansions === "notes";

      return (
        <button
          title="View notes"
          className="text-left w-full h-full flex items-center py-4 cursor-pointer"
          disabled={notesCount < 1}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const currentlyExpanded = row.getIsExpanded();
            const willCollapse = isExpanded && currentlyExpanded;

            if (willCollapse || !currentlyExpanded) {
              row.toggleExpanded();
            }
            onExpand(row.id, isExpanded ? null : "notes");
          }}
        >
          {notesCount > 0 ? (
            <>
              {notesCount}
              <Icon className="ml-1" name={isExpanded ? "expand_less" : "expand_more"} />
            </>
          ) : (
            "0"
          )}
        </button>
      );
    },
    size: 100
  };
}

// ==========================================
// PAT STATUS COLUMN BUILDER
// ==========================================

interface BuildPATStatusColumnProps<T> {
  getStatusTagColor: (status: string | null) => TagType;
  getPATStatus: (row: T) => string | null;
  /**
   * When true, the column header becomes a sortable control whose `id` matches
   * the backend `BackofficeEducationUniversityStudentsSortField.PAT_STATUS`
   * enum value for server-side sorting through `orderBy`.
   */
  sortable?: boolean;
}

/**
 * Builds a standardized PAT Status column for student tables
 * Displays PAT status as a colored tag with strong styling
 *
 * Note: Null/undefined status returns null to trigger Table's built-in empty state (--)
 *       All status tags use strong (filled) variant
 *
 */
export function buildPATStatusColumn<T>({
  getStatusTagColor,
  getPATStatus,
  sortable = false
}: BuildPATStatusColumnProps<T>) {
  return {
    accessorKey: "patStatus",
    ...(sortable ? { id: "PAT_STATUS" } : {}),
    header: "PAT Status",
    cell: ({ row }: { row: Row<T> }) => {
      const patStatus = getPATStatus(row.original);

      if (!patStatus) {
        return null;
      }

      return (
        <Tag tagType={getStatusTagColor(patStatus)} size="small">
          {patStatus}
        </Tag>
      );
    },
    ...(sortable
      ? { enableSorting: true, meta: { sortType: "text" as const, sortLabel: "PAT Status" } }
      : { enableSorting: false })
  };
}

// ==========================================
// COHORT COLUMN BUILDER
// ==========================================

interface BuildCohortColumnProps<T> {
  getStatusTagColor: (status: string | null) => TagType;
  getCohortValue: (row: T) => string | null;
  /**
   * Display mode for cohort value
   * - "tag": Display as a colored tag (for status values like "In-Cohort")
   * - "text": Display as plain text (for cohort names like "Fall 2025")
   */
  displayMode?: "tag" | "text";
  getActiveProbationStartDate?: (row: T) => string | null | undefined;
  getActiveProbationTooltip?: (row: T) => string | null | undefined;
  /**
   * When true, the column header becomes a sortable control whose `id`
   * matches the backend `BackofficeEducationUniversityStudentsSortField.COHORT`
   * enum so it can drive server-side sorting through `orderBy`.
   */
  sortable?: boolean;
}

/**
 * Builds a standardized Cohort column for student tables
 * Flexible to handle both status tags and plain text (future: "Fall 2025")
 * Shows empty state (--) when cohort value is null/undefined
 * Shows probation alert icon for students on active probation
 *
 * Usage:
 * ```typescript
 * buildCohortColumn({
 *   getStatusTagColor,
 *   getCohortValue: (row) => row.universityCohort?.name || "",
 *   displayMode: "text",
 *   getActiveProbationStartDate: (row) => studentProbationsMap.get(row.studentId) ?? null
 * })
 * ```
 */
export function buildCohortColumn<T>({
  getStatusTagColor,
  getCohortValue,
  displayMode = "text",
  getActiveProbationStartDate,
  getActiveProbationTooltip,
  sortable = false
}: BuildCohortColumnProps<T>) {
  return {
    id: sortable ? "COHORT" : "cohort-column",
    header: "Cohort",
    ...(sortable
      ? {
          accessorFn: (row: T) => getCohortValue(row) ?? "",
          enableSorting: true,
          meta: { sortType: "text" as const, sortLabel: "Cohort" }
        }
      : { enableSorting: false }),
    cell: ({ row }: { row: Row<T> }) => {
      const cohortValue = getCohortValue(row.original);
      const probationStartDate = getActiveProbationStartDate?.(row.original);
      const showProbationAlert = !!probationStartDate;
      const probationTooltip =
        getActiveProbationTooltip?.(row.original) ??
        (probationStartDate ? `Student on Probation (${toFormat(probationStartDate)})` : undefined);

      if (!cohortValue) {
        return (
          <div className="flex items-center gap-2">
            {showProbationAlert && probationTooltip && (
              <Tooltip
                supportingText={probationTooltip}
                variant="plain"
                color="black"
                anchorCorner="start-start"
                menuCorner="end-start"
              >
                <Icon name="error" className="text-sys-brand-error" />
              </Tooltip>
            )}
            <span className="opacity-20">--</span>
          </div>
        );
      }

      return (
        <div className="flex items-center gap-2">
          {showProbationAlert && probationTooltip && (
            <Tooltip
              supportingText={probationTooltip}
              variant="plain"
              color="black"
              anchorCorner="start-start"
              menuCorner="end-start"
            >
              <Icon name="error" className="text-sys-brand-error" />
            </Tooltip>
          )}
          {displayMode === "tag" ? (
            <Tag tagType={getStatusTagColor(cohortValue)} size="small">
              {cohortValue}
            </Tag>
          ) : (
            <span>{cohortValue}</span>
          )}
        </div>
      );
    },
    size: 160,
    minSize: 160
  };
}

// ==========================================
// NOTES HELPERS
// ==========================================

function findEnrollmentByStudentId(
  studentId: string,
  cohortData: GetCohortEnrollmentNotesByUniversityQuery | undefined
) {
  return cohortData?.university?.universityCohorts
    ?.flatMap((cohort) => cohort.enrollments)
    ?.find((enrollment) => enrollment.person.id === studentId);
}

/**
 * Extracts notes for a specific student from pre-loaded cohort data.
 * Returns properly typed CRMNote[] from the global type.
 *
 * @param studentId - The student's person ID (UUID)
 * @param cohortData - The cohort data from GET_COHORT_ENROLLMENT_NOTES_BY_UNIVERSITY_QUERY
 */
export function getStudentNotes(
  studentId: string,
  cohortData: GetCohortEnrollmentNotesByUniversityQuery | undefined
): CRMNote[] {
  return findEnrollmentByStudentId(studentId, cohortData)?.notes || [];
}

/**
 * Extracts the enrollment ID for a specific student from pre-loaded cohort data.
 *
 * @param studentId - The student's person ID (UUID)
 * @param cohortData - The cohort data from GET_COHORT_ENROLLMENT_NOTES_BY_UNIVERSITY_QUERY
 */
export function getStudentEnrollmentId(
  studentId: string,
  cohortData: GetCohortEnrollmentNotesByUniversityQuery | undefined
): string | undefined {
  return findEnrollmentByStudentId(studentId, cohortData)?.id;
}

// ==========================================
// EMAIL COLUMN BUILDER
// ==========================================

/**
 * Builds a standard Email column for student tables
 * Simple column that displays email address
 *
 */
export function buildEmailColumn() {
  return {
    accessorKey: "email",
    header: "Email",
    enableSorting: false
  };
}

// ==========================================
// INTERNSHIP MONTHS COLUMN BUILDER
// ==========================================

/**
 * Builds a standard Internship Months column for student tables
 * Displays internship months with empty state for null/undefined
 *
 */
export function buildInternshipMonthsColumn<T>() {
  return {
    accessorKey: "totalInternshipMonths",
    header: "Internship Months",
    cell: ({ row }: { row: Row<T> }) => {
      const months = (row.original as any).totalInternshipMonths;
      if (months == null || months === undefined) {
        return <span className="opacity-20">--</span>;
      }

      return months.toFixed(1);
    },
    enableSorting: false
  };
}

// ==========================================
// ENROLLMENT DATE COLUMN BUILDERS
// ==========================================

/**
 * Builds a standardized Enroll Start Date column
 * Shows formatted date or empty state (--) if no date
 *
 */
export function buildEnrollStartDateColumn<T>() {
  return {
    accessorKey: "enrollStartDate",
    header: "Enroll Start Date",
    enableSorting: false,
    cell: ({ row }: { row: Row<T> }) => {
      const date = (row.original as any).enrollStartDate;
      return date ? toFormat(date) : <span className="opacity-20">--</span>;
    }
  };
}

/**
 * Builds a standardized Enroll End Date column
 * Shows formatted date or empty state (--) if no date
 *
 */
export function buildEnrollEndDateColumn<T>() {
  return {
    accessorKey: "enrollEndDate",
    header: "Enroll End Date",
    enableSorting: false,
    cell: ({ row }: { row: Row<T> }) => {
      const date = (row.original as any).enrollEndDate;
      return date ? toFormat(date) : <span className="opacity-20">--</span>;
    }
  };
}

// ==========================================
// PROGRAM LEVEL COLUMN BUILDER (WITH EXPANDER)
// ==========================================

interface ProgramData {
  version: string;
  level: string;
  completed?: boolean;
}

interface BuildProgramLevelColumnProps<T> {
  getProgram: (row: T) => ProgramData;
  /**
   * Optional: For dual expansion support (e.g., tables with both Notes and Program Levels)
   * Provide the expansion state object and callback
   */
  expanded?: ExpansionState;
  onExpand?: (rowId: string, column: ExpandedColumn) => void;
  /**
   * Optional: The key to use in expansion state (default: "programLevels")
   * Only used when expanded/onExpand are provided
   */
  expandedKey?: Exclude<ExpandedColumn, null>;
  /**
   * Optional: Custom header text (default: "Program / Level")
   */
  header?: string;
  /**
   * Optional: Display mode for program info
   * - "full": Shows "PGM {version} {completed ? 'Completed' : 'Level {level}'}" (default)
   * - "version": Shows only "PGM {version}"
   */
  displayMode?: "full" | "version";
}

/**
 * Builds a standard Program / Level column with expand functionality
 * Supports both single expansion and dual expansion modes
 *
 * Usage (single expansion):
 * ```typescript
 * buildProgramLevelColumn({
 *   getProgram: (row) => row.program
 * })
 * ```
 *
 * Usage (dual expansion):
 * ```typescript
 * buildProgramLevelColumn({
 *   getProgram: (row) => row.program,
 *   expanded,
 *   onExpand,
 *   expandedKey: "programLevels"
 * })
 * ```
 */
export function buildProgramLevelColumn<T>({
  getProgram,
  expanded,
  onExpand,
  expandedKey = "programLevels",
  header = "Program / Level",
  displayMode = "full"
}: BuildProgramLevelColumnProps<T>) {
  const getProgramText = (program: ProgramData) => {
    if (displayMode === "version") {
      return `PGM ${program.version}`;
    }
    return `PGM ${program.version} ${program.completed ? "Completed" : `Level ${program.level}`}`;
  };

  return {
    accessorKey: "program",
    header,
    enableSorting: false,
    cell: ({ row }: { row: Row<T> }) => {
      const program = getProgram(row.original);

      if (expanded && onExpand) {
        const rowExpansions = expanded[row.id];
        const isExpanded = rowExpansions === expandedKey;
        const shouldShowIcon = !isCompactLayout();

        return (
          <button
            type="button"
            title="View program levels"
            className="flex items-center gap-2 cursor-pointer appearance-none border-0 bg-transparent p-0 font-inherit text-inherit text-start"
            aria-expanded={isExpanded}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const currentlyExpanded = row.getIsExpanded();
              const willCollapse = isExpanded && currentlyExpanded;

              if (willCollapse || !currentlyExpanded) {
                row.toggleExpanded();
              }
              onExpand(row.id, isExpanded ? null : expandedKey);
            }}
          >
            <span>{getProgramText(program)}</span>
            {shouldShowIcon && <Icon name={isExpanded ? "expand_less" : "expand_more"} />}
          </button>
        );
      }

      const isExpanded = row.getIsExpanded();
      const shouldShowIcon = !isCompactLayout();

      return (
        <button
          type="button"
          data-testid={`expand-row-${row.id}`}
          className="flex items-center gap-2 cursor-pointer appearance-none border-0 bg-transparent p-0 font-inherit text-inherit text-start"
          aria-expanded={isExpanded}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            row.getToggleExpandedHandler()();
          }}
        >
          <span>{getProgramText(program)}</span>
          {shouldShowIcon && <Icon name={isExpanded ? "expand_less" : "expand_more"} />}
        </button>
      );
    },
    minSize: 200
  };
}

// ==========================================
// ROW LINK HELPERS
// ==========================================

export type UniversityStudentDetailSubPath = "internships" | "test-results" | "pat-results";

export function buildUniversityStudentDetailLink({
  universityId,
  pgaId,
  returnPath,
  searchParamsUrl = "",
  subPath
}: {
  universityId: string;
  pgaId: string;
  returnPath: string;
  searchParamsUrl?: string;
  subPath?: UniversityStudentDetailSubPath;
}): { to: string } {
  const returnUrl = encodeURIComponent(returnPath);
  const separator = searchParamsUrl ? "&" : "";
  const query = searchParamsUrl ? `${searchParamsUrl}${separator}returnUrl=${returnUrl}` : `returnUrl=${returnUrl}`;
  const pathSuffix = subPath ? `/${subPath}` : "";

  return {
    to: `/education/universities/${universityId}/students/${pgaId}${pathSuffix}?${query}`
  };
}

// ==========================================
// PROGRAM LEVEL COLUMNS (FOR TABLE DETAILS PANEL)
// ==========================================

/**
 * Builds standardized program level columns for TableDetailsPanel
 * Used in expanded rows to show program level details
 *
 */
export function buildProgramLevelColumns(getStatusTagColor: (status: string) => TagType) {
  return [
    buildTwoLineColumn({
      accessorKey: "level",
      header: "Level",
      getPrimaryText: (row: any) => `Level ${row.level}`,
      getSecondaryText: (row: any) => `Code: ${row.levelCode || "--"}`
    }),
    {
      accessorKey: "startDate",
      header: "Start Date",
      cell: ({ row }: { row: any }) =>
        row.original.startDate ? toFormat(row.original.startDate) : <span className="opacity-20">--</span>
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      cell: ({ row }: { row: any }) =>
        row.original.endDate ? toFormat(row.original.endDate) : <span className="opacity-20">--</span>
    },
    {
      accessorKey: "status",
      header: "Program Status",
      meta: {
        headerClassName: "hide-header-in-column"
      },
      cell: ({ row }: { row: any }) => {
        const completed = row.original.completed;
        const status = completed ? "Completed" : "Active";
        const tagType = status === "Active" ? "warn" : getStatusTagColor(status);
        return (
          <Tag tagType={tagType} size="small">
            {status}
          </Tag>
        );
      }
    }
  ];
}
